/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2020, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import * as _ from 'underscore';
import { Logger, MessageService, ModalService } from '@core/lib/services';
import { Injectable, Injector } from '@angular/core';
import { UtilService } from '@app/util/util.service';
import { WorkItem } from '@app/data-model/item/work-item';
import { IntegrationStatusService } from '@app/di/integration-status.service';
import { appConstants } from '@app/common/app-constants';
import { EventMessage, Events, EventService } from '@app/common/event';
import { EventWorkItem } from '@app/data-model/event-work-item';
import { UserPreferenceService } from '@app/common/user-preference.service';
import { DiConfigService } from '@app/di/di-config.service';
import { UserPrivilegesService } from '@app/common/user-privileges.service';
import { AppConfigService } from '@app/common/app-config.service';
import { SearchFilterCriteriaService } from '@app/ui/search/search-filter-criteria.service';
import { DataTransformationService } from '@app/common/data-transformation.service';
import { ProxyDataService } from '@app/core/http';
import { ExamTagUpdate } from '@app/data-model/exam-tag-update';
import { IndicatorUpdate } from '@app/data-model/indicator-update';
import { ToggleKnobService } from '@app/ui/common/toggle-knob-service';
import { AssignToMeConfirmationComponent } from '@app/common/modals/components/assign-to-me-confirmation/assign-to-me-confirmation.component';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { StateUpdate } from '@app/data-model/state-update';
import { StatusUpdate } from '@app/data-model/status-update';
import { ExamFlagsUpdate } from '@app/data-model/exam-flags-update';
import { ArchiveAdoptedService } from '@app/common/services/bring-online/archive-adopted.service';
import { REPORT_STATUS } from '@app/di/integrations/reporting/reporting-contants';
import { Deferred } from '@app/common/deferred';
import { DesignatedAuthorUpdate } from '../designated-author-update';
import { PreAction, ViewerPreAction } from '@app/di/integrations/pre-action.decorator';

const log = new Logger('WorkItemService');

@Injectable({
  providedIn: 'root'
})
export class WorkItemService {
  private REPORTING_CONFIG;
  private launchedFromAssignToMeWorkItemId;
  private extStatusFlag = [1, 9, 6];
  private launchedFromAssignToMeDialog;

  constructor(
    private utilService: UtilService,
    private eventService: EventService,
    private messageService: MessageService,
    private translate: TranslateService,
    private modalService: ModalService,
    private dataService: ProxyDataService,
    private integrationStatusService: IntegrationStatusService,
    private userPreferenceService: UserPreferenceService,
    private userPrivilegesService: UserPrivilegesService,
    private diConfigService: DiConfigService,
    private appConfigService: AppConfigService,
    private searchFilterCriteriaService: SearchFilterCriteriaService,
    private dataTransformationService: DataTransformationService,
    private toggleKnobService: ToggleKnobService,
    private archiveAdoptedService: ArchiveAdoptedService,
    public injector: Injector
  ) {
    this.initialize();
  }

  initialize() {
    this.REPORTING_CONFIG = this.diConfigService.REPORTING_CONFIG;
  }

  launchExam(item, launchedFromAssignToMeDialog = false): Promise<any> {
    this.launchedFromAssignToMeDialog = launchedFromAssignToMeDialog;
    if (
      item.state &&
      item.state.isAssignedToOther &&
      !item.state.isActivated &&
      ((this.canSelfAssign() &&
        this.extStatusFlag.indexOf(item.state.ownership.assignedToUser[0]['extStatusFlag']) >= 0) ||
        (this.filterSortUserList(item, [6]) !== false && !this.launchedFromAssignToMeDialog))
    ) {
      const sub: Subscription = this.modalService
        .openModal(AssignToMeConfirmationComponent, {
          headerContent: this.translate.instant('assignToMe.modalTitle'),
          data: {
            item: item
          }
        })
        .afterClosed.subscribe(result => {
          if (!result) {
            this.messageService.broadcast('clearActivatedItem');
          }
          sub.unsubscribe();
        });
      this.messageService.broadcast('assignToMe.opened');
      return Promise.reject();
    }

    return this.activate(item).then(() => {
      this.toggleKnobService.openDiagnosticHub();
    });
  }

  previewExam(item): Promise<any> {
    return this.select(item).then(() => {
      this.toggleKnobService.openDiagnosticHub();
    });
  }

  @PreAction('clearContext')
  @ViewerPreAction('home')
  select(workItem: WorkItem, extraParams?: any): Promise<any> {
    const tab = this.utilService.getTab(workItem.id);
    if (tab && extraParams) {
      tab.isLaunchedFromPtHistory = extraParams.source !== 'patient-history';

      this.messageService.broadcast('openExam', tab);
      return Promise.resolve(undefined);
    }

    const eventWorkItem = this.getEventFrameworkTarget(workItem);
    if (this.tabLimitReachedOrRequestPending(eventWorkItem.id)) {
      log.info(
        'Exam is not selected; either there are already maximum allowed exam tabs open or ' +
          'integration request is pending. Work item id: ' +
          eventWorkItem.id
      );
      return Promise.reject(appConstants.MAX_TAB_ERROR + ' or ' + appConstants.PENDING_RESPONSE);
    }

    if (eventWorkItem.id.toString() === appConstants.externalTabId) {
      return this.eventService.sendEvent(new EventMessage(Events.externalAccessTab, eventWorkItem));
    } else {
      return this.eventService.sendEvent(new EventMessage(Events.preview, eventWorkItem));
    }
  }

  create(workItem: WorkItem, extraParams?: any): Promise<any> {
    const tab = this.utilService.getTab(workItem.id);
    if (tab && extraParams) {
      tab.isLaunchedFromPtHistory = extraParams.source !== 'patient-history';
      return Promise.resolve(undefined);
    }

    const eventWorkItem = this.getEventFrameworkTarget(workItem);
    if (this.tabLimitReachedOrRequestPending(eventWorkItem.id)) {
      log.info(
        'Exam is not selected; either there are already maximum allowed exam tabs open or ' +
          'integration request is pending. Work item id: ' +
          eventWorkItem.id
      );
      return Promise.reject(appConstants.MAX_TAB_ERROR + ' or ' + appConstants.PENDING_RESPONSE);
    }
    this.messageService.broadcast('createTab', eventWorkItem);
    return Promise.resolve(eventWorkItem);
  }

  launchReadonly(workItem: WorkItem, params?: any) {
    const eventWorkItem = this.getEventFrameworkTarget(workItem);
    if (this.tabLimitReachedOrRequestPending(eventWorkItem.id)) {
      log.info(
        'Exam is not selected; either there are already maximum allowed exam tabs open or ' +
          'integration request is pending. Work item id: ' +
          eventWorkItem.id
      );
      return Promise.reject();
    }

    if (!params) {
      params = _.extend({}, params);
    }
    params.launchOfflinePopup =
      params.launchOfflinePopup === undefined ? this.isLaunchOfflinePopup(workItem) : params.launchOfflinePopup;
    return this.eventService.sendEvent(new EventMessage(Events.launchreadonly, eventWorkItem, params));
  }

  @PreAction('clearContext')
  @ViewerPreAction('open')
  activate(workItem: WorkItem, params?: any): Promise<any> {
    // params.doAutoAdvance = true, will skip integration status check during auto advance
    if (this.launchedFromAssignToMeDialog) {
      this.launchedFromAssignToMeWorkItemId = workItem.id;
      this.launchedFromAssignToMeDialog = false;
    }

    params = _.extend({}, params);
    const tab = this.utilService.getTab(workItem.id);
    if (tab) {
      tab.isLaunchedFromPtHistory = params.source === 'patient-history';
      if (tab.examContext === 'read') {
        this.messageService.broadcast('openExam', tab);
        return Promise.reject('Exam launched from patient-history');
      }
    }

    params.launchOfflinePopup =
      params.launchOfflinePopup === undefined ? this.isLaunchOfflinePopup(workItem) : params.launchOfflinePopup;
    const eventWorkItem = this.getEventFrameworkTarget(workItem, params.studyClickTime);
    if (!params.isAutoAdvance && this.tabLimitReachedOrRequestPending(eventWorkItem.id)) {
      return Promise.reject(
        'Exam is not activated; either there are already maximum allowed exam tabs open or ' +
          'integration request is pending. Work item id: ' +
          eventWorkItem.id
      );
    }

    const launchReadonly = new Deferred();
    this.eventService
      .sendEvent(new EventMessage(Events.launchreadonly, { workItem: eventWorkItem }, params))
      .catch(ex => {
        log.error(
          `Error occurred while executing launchreadonly event, id - ${eventWorkItem.id}, Exception - ${JSON.stringify(
            ex
          )}`
        );
      })
      .finally(() => {
        log.debug('Work-item service : Launch read only event was completed successful.');
        launchReadonly.resolve('Launch read only completed.');
      });

    const deferred = new Deferred();
    if (this.canSelfAssign() && this.doReadLock(eventWorkItem)) {
      this.eventService
        .sendEvent(new EventMessage(Events.read, { workItem: eventWorkItem }, params))
        .then(() => {
          log.debug('Work-item service : Read event was successful.');
          deferred.resolve(workItem);
        })
        .catch(ex => {
          log.error('Error occurred while executing read event, id -', eventWorkItem.id, 'Exception -', ex);
          deferred.reject(workItem);
        });
    } else if (!UtilService.isRemoteExam(workItem) && !(workItem.state && workItem.state.isAssigned)) {
      if (this.canSelfAssign()) {
        this.eventService
          .sendEvent(new EventMessage(Events.assign, { workItem: eventWorkItem }, params))
          .then(() => {
            log.debug('Work-item service : Assign event was successful.');
            deferred.resolve(workItem);
          })
          .catch(ex => {
            log.error('Error occurred while executing assign event, id -', eventWorkItem.id, 'Exception -', ex);
            deferred.reject(workItem);
          });
      }
    }
    return Promise.resolve({ eventWorkItem: eventWorkItem, getResolved: [deferred.promise, launchReadonly] });
  }

  private canSelfAssign() {
    return (
      this.userPrivilegesService.hasPrivilege('Dictate') || this.userPrivilegesService.hasPrivilege('ID_Assignment')
    );
  }

  takeControl(workItem: WorkItem, config?: any) {
    // If report status was pending before taking control, the report is already deleted
    if (workItem.reportStatus === REPORT_STATUS.PENDING || workItem.reportStatus === REPORT_STATUS.PENDING_TEMPORARY) {
      workItem.reportStatus = 0;
      workItem.reportId = null;
      workItem.initialAuthor = null;
    }

    const eventWorkItem = this.getEventFrameworkTarget(workItem);
    if (this.tabLimitReachedOrRequestPending(eventWorkItem.id)) {
      return Promise.reject();
    }

    eventWorkItem.keys.launchDictation = true;
    if (config && config.takeControl) {
      if (config.isAssociableExam) {
        eventWorkItem.requestedProcedureId = eventWorkItem.id;
        return this.eventService.sendEvent(
          new EventMessage(Events.read, eventWorkItem, {
            source: 'desktop-integration',
            isTakeControl: true
          })
        );
      } else {
        // Set the flag to skip the launchreadonly action for ps integration
        eventWorkItem.keys.launchDictation = true;
        this.eventService.sendEvent(new EventMessage(Events.launchreadonly, eventWorkItem));
        return new Promise((resolve, reject) => {
          this.eventService.sendEvent(new EventMessage(Events.takeControl, eventWorkItem)).then(
            data => {
              resolve(data);
            },
            error => {
              reject(error);
            }
          );
        });
      }
    }
  }

  getEventFrameworkTarget(workItem: WorkItem, studyClickTime?: any) {
    this.REPORTING_CONFIG = this.diConfigService.REPORTING_CONFIG;
    const reportStatus = workItem.reportStatusId ? workItem.reportStatusId : workItem.reportStatus;
    const params = {
      procedureAccessionNo: workItem.accession,
      studyDataAvailability: workItem.studyDataAvailability,
      requestedProcedureId: workItem.id,
      mrn: workItem.patientIdentifier,
      nhi: workItem.nhi,
      patientName: workItem.patientName ? workItem.patientName.text : undefined,
      ideographicName: workItem.ideographicName ? workItem.patientName.ideographicName : undefined,
      phoneticName: workItem.phoneticName ? workItem.patientName.phoneticName : undefined,
      patientVipCode: workItem.patientVipCode,
      imageCount: workItem.imageCount,
      procedurePriority: workItem.priority,
      procedurePriorityCode: workItem.priorityId,
      priorityBadgeColor: workItem.priorityColor,
      procedureStatus: workItem.status,
      procedureStatusId: workItem.statusId,
      rejectedImageCount: workItem.rejectedImageCount,
      // Invoke getKeyIdentifierFromConfig to get site name key if multi-site is enabled.
      siteNameKey:
        this.REPORTING_CONFIG.SINGLESITE === 'false'
          ? this.getKeyIdentifierFromConfig(this.REPORTING_CONFIG.SITENAME_KEY_LOCATION, workItem)
          : undefined,
      // Invoke getKeyIdentifierFromConfig to get viewer identifier if multi-viewer is enabled.
      studyClickTime: this.getStudyClickTime(studyClickTime),
      // properties for DxHub ExamTab Info
      timeOfExam: workItem.timeOfExam,
      modality: workItem.modality,
      procedureCode: workItem.procedureCode,
      procedureText: workItem.procedureText,
      patientNameObj: workItem.patientName,
      state: workItem.state,
      examSaved: workItem.examSaved,
      instituteId: workItem.instituteId,
      examNoteCount: workItem.examNoteCount,
      patientId: workItem.patientId,
      patientDoB: workItem.patientDoB,
      patientAge: workItem.patientAge,
      patientGender: workItem.patientGender,
      protocolStat: workItem.protocolStat,
      protocolWorklist: workItem.protocolWorklist,
      archiveAdoptedStatus: workItem.archiveAdoptedStatus,
      archiveFetchStatus: workItem.archiveFetchStatus,
      examTagDetails: workItem.examTagDetails,
      repositoryType: workItem.repositoryType,
      repositoryName: workItem.repositoryName,
      repositoryId: workItem.repositoryId,
      siteId: workItem.site,
      currentLocation: workItem.currentLocation,
      indication: workItem.indication,
      orderingLocation: workItem.orderingLocation,
      referringPhysician: workItem.referringPhysician,
      procedureImprText: workItem.procedureImprText,
      aiFinding: workItem.aiFinding,
      setContextByExternalApp: workItem.setContextByExternalApp,
      setContextByGenEx: workItem.setContextByGenEx,
      disableAutoLaunchViewer: this.isDisableLaunchViewer(workItem),
      launchDictation: this.canLaunchDictation(workItem),
      aiStatus: workItem.aiStatus,
      exceptions: workItem.exceptions,
      source: workItem.source,
      procedureStudyInstanceUID: workItem.procedureStudyInstanceUID,
      studyLevelStudyInstanceUID: workItem.studyLevelStudyInstanceUID,
      bodyRegion: workItem.bodyRegion,
      reportId: workItem.reportId,
      reportStatus: reportStatus,
      authorId: workItem.authorId,
      lastAuthorId: workItem.lastAuthorId,
      designatedAuthor: workItem.designatedAuthor,
      nextApproverId: workItem.nextApproverId,
      authorName: this.searchFilterCriteriaService.getAuthorUserName(workItem.authorId),
      eventComment: workItem.eventComment,
      organizationId: workItem.managingOrganizationId,
      isOpenedFromUrl: workItem.isOpenedFromUrl
    };
    return new EventWorkItem(workItem.id, params, !!workItem.takeOwnership);
  }

  handleDomainSysCode(workItem) {
    if (workItem.patientIdentifier) {
      this.extractedHandleDomainSysCode(workItem);
    }
  }

  extractedHandleDomainSysCode(workItem) {
    workItem.patientIdentifier.forEach(identifier => {
      identifier.display = this.appConfigService.getDomainSysCode(identifier.system);
    });
  }

  canLaunchDictation(workItem: WorkItem) {
    if (workItem.setContextByExternalApp) {
      return true;
    }

    const dictatePrivilege = this.userPrivilegesService.hasPrivilege('Dictate');
    const dictateUserPref = this.userPreferenceService.getUserPreferenceFromCache('dictate') === true;
    const isNativeReporting = this.integrationStatusService.isNativeReportingEnabled();

    return (
      dictatePrivilege &&
      dictateUserPref &&
      !workItem.protocolWorklist &&
      (isNativeReporting || workItem.statusId !== appConstants.COMPLETED_STATUS_ID) &&
      workItem.state &&
      (workItem.state.ownership === undefined ||
        workItem.state.ownership.assignedToUser.length === 0 ||
        (!workItem.state.isAssignedToOther &&
          (workItem.state.extAppWorkItemState === appConstants.RA1000_APPROVER_ADDED ||
            !workItem.state.ownership.assignedToUser[0].externalUser)) ||
        workItem.state.extAppWorkItemState === appConstants.EXTERNAL_READ_LOCK_ADDED) &&
      !UtilService.isRemoteExam(workItem)
    );
  }

  getStudyClickTime(studyClickTime) {
    if (!studyClickTime) {
      studyClickTime = new Date().getTime();
    }
    log.info('WorkItem - getStudyClickTime : ' + studyClickTime);
    return studyClickTime;
  }

  getKeyIdentifierFromConfig(keyLocation, workItem: WorkItem) {
    try {
      log.info(
        'WorkItem - getKeyIdentifierFromConfig ORGANIZATIONMAP : ' +
          JSON.stringify(this.diConfigService.DI_CONFIG.ORGANIZATIONMAP) +
          ', SITENAME_KEY_LOCATION : ' +
          keyLocation
      );
      const keyLocationArray = keyLocation.split(';');
      switch (keyLocationArray.length) {
        // Find site name using given site name key location like orderingLocation, procedureCode, etc
        case 1: {
          const siteNameKey = this.getValueWithDynamicKey(keyLocationArray[0], workItem);
          if (siteNameKey !== undefined) {
            return siteNameKey;
          }
          log.error('WorkItem - getKeyIdentifierFromConfig : Invalid siteName key in configuration : ' + keyLocation);
          return undefined;
        }
        // Read site name key from organizationMap for given orgId and orgType.
        case 3: {
          let organizationId;
          let organizationType;
          for (let arrayIndex = 1; arrayIndex < keyLocationArray.length; arrayIndex++) {
            if (keyLocationArray[arrayIndex] === 'managingOrganizationId') {
              organizationId = this.getValueWithDynamicKey(keyLocationArray[arrayIndex], workItem);
            }
            if (keyLocationArray[arrayIndex] === 'managingOrganizationType') {
              organizationType = this.getValueWithDynamicKey(keyLocationArray[arrayIndex], workItem);
            }
          }
          if (organizationId !== undefined && organizationType !== undefined) {
            log.info(
              'Workitem getKeyIdentifierFromConfig - OrganizationId : ' +
                organizationId +
                ', ' +
                'OrganizationType : ' +
                organizationType
            );
            return this.getOrganizationNameFromMap(organizationId, organizationType);
          }
          log.warn(
            'WorkItem - getKeyIdentifierFromConfig : managingOrganizationId/managingOrganizationType ' +
              'field not found in configuration/workitem : ' +
              keyLocation
          );
          return undefined;
        }
        default:
          log.error(
            'WorkItem - getKeyIdentifierFromConfig : Unsupported SiteName key location format in ' +
              'configuration : ' +
              keyLocation
          );
          return undefined;
      }
    } catch (e) {
      log.error('WorkItem - Exception in getKeyIdentifierFromConfig : ' + e.description);
      return undefined;
    }
  }

  getValueWithDynamicKey(keyLocation, workItem: WorkItem) {
    try {
      const keyArray = keyLocation.split('.');
      let key = keyArray.length > 0 ? workItem[keyArray[0]] : undefined;
      for (let arrayIndex = 1; arrayIndex < keyArray.length; arrayIndex++) {
        key = key[keyArray[arrayIndex]];
      }
      log.info('getValueWithDynamicKey WorkItem Value : ' + key);
      return key;
    } catch (e) {
      log.error('Exception in getValueWithDynamicKey : ' + e.description);
      return undefined;
    }
  }

  getOrganizationNameFromMap(organizationId, organizationType) {
    const commonConfig = this.diConfigService.DI_CONFIG;
    const organizationItem = {};
    const idToSearch = 'organizationId';
    if (!_.isUndefined(organizationId) && !_.isUndefined(organizationType)) {
      organizationItem[idToSearch] = organizationId;
      log.info(
        'WorkItem - getOrganizationNameFromMap - ORGANIZATION_MAP' + JSON.stringify(commonConfig.ORGANIZATIONMAP)
      );
      if (!_.isUndefined(commonConfig.ORGANIZATIONMAP)) {
        const organization = _.findWhere(commonConfig.ORGANIZATIONMAP, organizationItem) as any;
        if (!_.isUndefined(organization)) {
          return organization.organizationName;
        } else {
          log.error(
            'WorkItem - getOrganizationNameFromMap : Organization Id/Type is not present in ' +
              'ORGANIZATIONMAP, Organization Id: ' +
              organizationId +
              ', Organization Type: ' +
              organizationType
          );
          return '';
        }
      }
    } else {
      log.error(
        'WorkItem - getOrganizationNameFromMap : Organization Id/Type is undefined, Organization Id: ' +
          organizationId +
          ', Organization Type: ' +
          organizationType
      );
    }
    return undefined;
  }

  isDisableLaunchViewer(workItem: WorkItem) {
    if (workItem.setContextByGenEx) {
      return false;
    }

    let result;
    const activeWorklist = this.appConfigService.getActiveList();
    const isCustomWorklist = activeWorklist !== undefined && !!activeWorklist.isCustom;

    if (workItem.setContextByExternalApp || workItem.useAutoLaunchUserPreference) {
      result = !this.userPreferenceService.getUserPreferenceFromCache('autoLaunchViewerFromExternalSystem');
    } else if (workItem.id === this.launchedFromAssignToMeWorkItemId) {
      result = false;
    } else if (isCustomWorklist) {
      result = !activeWorklist.autoLaunchViewer;
    } else {
      result =
        activeWorklist !== undefined &&
        activeWorklist.preference &&
        activeWorklist.preference.key === 'viewer' &&
        !this.userPreferenceService.getUserPreferenceFromCache('viewer');
      if (result === undefined) {
        result = false;
      }
    }
    return result;
  }

  tabLimitReachedOrRequestPending(id) {
    return !this.integrationStatusService.canOpenNewTab(id) || this.integrationStatusService.isPendingRequest(id);
  }

  doReadLock(workItem: EventWorkItem) {
    // Get read for exams in protocol work list irrespective of dictation preference and if completed exam is
    // launched from normal worklist then get assignment lock.
    return workItem.keys.protocolWorklist || workItem.keys.launchDictation || workItem.keys.setContextByExternalApp;
  }

  generateExternalId(item: any) {
    const id = {
      accession: item.accession,
      patientId: '',
      domain: this.appConfigService.getUserDefaultDomain()
    };

    if (item.patientIdentifier) {
      const tempMrn = this.dataTransformationService.filterDomainIdentifier(
        this.appConfigService.getUserDefaultDomain(),
        item.patientIdentifier
      );
      id.patientId = tempMrn ? tempMrn.domainIdentifier : '';
    }
    return id;
  }

  /**
   * Fetches the exam tag details for the specified ids.
   * @param workItemIds [number] Workitem ids to fetch exam tag details.
   * @param worklistId number The id of the worklist containing these workitems. Used for perf logging.
   * @returns {deferred.promise} Promise that will resolve with [ExamTagUpdate],
   *                             or reject if there was an error.
   */
  getWorkItemExamTagDetails(workItemIds) {
    const getParams = { ids: workItemIds };
    return new Promise((resolve, reject) => {
      this.dataService.get('exam-tag', getParams).then(
        (examtagResponse: Array<any>) => {
          const examTagDetails = [];
          //update the workitems through the page or through this list.
          for (let j = 0; j < examtagResponse.length; ++j) {
            examTagDetails.push(new ExamTagUpdate(examtagResponse[j].examId.toString(), examtagResponse[j]));
          }
          resolve(examTagDetails);
        },
        error => {
          log.error('Error occurred while getting exam-tags, Error - ', JSON.stringify(error));
          reject(error);
        }
      );
    });
  }

  /**
   * Fetches the workitem indicators for the specified ids.
   * @param workItemIds [number] Workitem ids to fetch indicators.
   * @param worklistId number The id of the worklist containing these workitems. Used for perf logging.
   * @returns {deferred.promise} Promise that will resolve with [IndicatorUpdate],
   *                             or reject if there was an error.
   */
  getWorkItemIndicators(workItemIds: number[]) {
    const getParams = {
      ids: workItemIds,
      examNotesDisabled: false
    };
    if (!(this.userPrivilegesService.hasPrivilege('ViewNote') && this.appConfigService.getExamNotesEnabled())) {
      getParams.examNotesDisabled = true;
    }
    return new Promise((resolve, reject) => {
      this.dataService.get(appConstants.appURLs.indicators, getParams).then(
        indicatorsResponse => {
          const indicators = {} as any;
          //update the workitems through the page or through this list.
          for (let i = 0; i < workItemIds.length; ++i) {
            const workItemId = workItemIds[i];
            let update: IndicatorUpdate = undefined;
            if (indicatorsResponse[workItemId]) {
              update = new IndicatorUpdate(
                workItemId.toString(),
                indicatorsResponse[workItemId].examSaved,
                indicatorsResponse[workItemId].examNoteCount
              );
            } else {
              update = new IndicatorUpdate(workItemId.toString(), undefined, undefined);
            }

            indicators[workItemId] = update;
          }
          resolve(indicators);
        },
        error => {
          reject(error);
        }
      );
    });
  }

  /**
   * Combines the resulting Updates from the promises into a map.
   * @param promises [deferred.promise] Array of promises that should resolve with Updates.
   * @param errorCallback Function to handle promise rejections.
   * @returns {deferred.promise} Promise that will resolve with a map from workitem id (as number) to [Update],
   *                             or reject if no results.
   */
  collatePromises(promises: Promise<any>[], errorCallback?): Promise<any> {
    errorCallback = errorCallback || appConstants.noop;
    let callCount = 0;
    const map = {} as any;

    return new Promise((resolve, reject) => {
      promises.forEach((promise: Promise<any>) => {
        promise
          .then(updates => {
            ++callCount;
            if (Array.isArray(updates)) {
              updates.forEach((update: any) => {
                map[update.id] = map[update.id] || [];
                map[update.id].push(update);
              });
            } else {
              _.forEach(updates, update => {
                map[update.id] = map[update.id] || [];
                map[update.id].push(update);
              });
            }
            if (callCount === promises.length) {
              if (_.isEmpty(map)) {
                reject();
              } else {
                resolve(map);
              }
            }
          })
          .catch(ex => {
            ++callCount;
            errorCallback(ex);
            if (callCount === promises.length) {
              if (_.isEmpty(map)) {
                reject();
              } else {
                resolve(map);
              }
            }
            log.error('Exception occurred while getting work items data, Ex - ', JSON.stringify(ex));
          });
      });
    });
  }

  createSavedWorkItems(items) {
    const savedItems = [];
    items &&
      items.forEach((savedItem: any) => {
        if (savedItem && savedItem.item) {
          const workItem = this.createWorkItem({ workItem: savedItem.item });
          workItem.savedExamId = savedItem.id;
          workItem.savedExamText = savedItem.description;
          savedItems.push(workItem);
        }
      });
    return savedItems;
  }

  createWorkItem(item): WorkItem {
    const wItem = this.getWorkItem(item);
    return (wItem.workItem ? wItem.workItem : wItem) as any;
  }

  createWorkItems(id, items: any[], parameters?: any): WorkItem[] {
    if (items && items.length) {
      const workItems = [];
      items.forEach((item: any) => {
        const workItem = this.createWorkItem(item);
        workItem.protocolWorklist = parameters?.protocolWorklist;
        workItems.push(workItem);
      });
      return workItems;
    }
  }

  getIconDetailsForWorkItems(workItems: WorkItem[], list?: any): Promise<{ list: any; updates: Promise<any> }> {
    const workItemIds = [];
    const workItemExternalIds = [];
    const workItemToExternalIdMap = {};
    workItems &&
      workItems.forEach((workItem: any) => {
        // indicators are not applicable for remote exams and global exams.
        if (!UtilService.isRemoteExam(workItem) && !UtilService.isGlobalExam(workItem)) {
          workItemIds.push(workItem.id);
          if (workItem.accession) {
            const externalId = this.generateExternalId(workItem);
            workItemToExternalIdMap[workItem.id] = externalId;
            workItemExternalIds.push(externalId);
          }
        }
      });

    return new Promise<{ list: any; updates: Promise<any> }>(resolve => {
      const promises: Promise<any>[] = [];
      if (workItemIds.length) {
        promises.push(
          this.dataService.getWorkItemStates(workItemIds, this.appConfigService.getUserId()).catch(ex => {
            log.error(
              `Error occurred while get workItemStates, ids - ${workItemIds}, Exception - ${JSON.stringify(ex)}`
            );
          })
        );
        promises.push(
          this.getWorkItemExamTagDetails(workItemIds).catch(ex => {
            log.error(
              `Error occurred while getting exam tag details, ids - ${workItemIds}, Exception - ${JSON.stringify(ex)}`
            );
          })
        );
        promises.push(
          this.getWorkItemIndicators(workItemIds).catch(ex => {
            log.error(
              `Error occurred while getting work-item indicators, ids - ${workItemIds}, Exception - ${JSON.stringify(
                ex
              )}`
            );
          })
        );
      }

      if (!list) {
        list = this.appConfigService.getActiveList();
      }
      if (promises.length) {
        const updatesPromise: Promise<any> = this.collatePromises(promises).catch(appConstants.noop);
        resolve({ list, updates: updatesPromise });
      } else {
        resolve({ list, updates: undefined });
      }
    });
  }

  getWorkItemStates(workItems: any[]): Promise<any> {
    const workItemIds = [];
    workItems &&
      workItems.forEach((workItem: any) => {
        if (!UtilService.isRemoteExam(workItem)) {
          workItemIds.push(workItem.id);
        }
      });
    if (workItemIds.length) {
      return this.dataService
        .getWorkItemStates(workItemIds, this.appConfigService.getUserId())
        .then((stateUpdates: any[]) => {
          if (stateUpdates.length) {
            stateUpdates.forEach((state: any) => {
              const item = _.find(workItems, workItem => {
                if (workItem.id.toString() === state.id) {
                  return workItem;
                }
              });
              if (item) {
                item.state = state.newState;
              }
            });
          }
        })
        .catch(ex => {
          log.error(`Error occurred while get workItemStates, ids - ${workItemIds}, Exception - ${JSON.stringify(ex)}`);
        });
    }
    return Promise.reject();
  }

  getWorkItem(data: any): WorkItem {
    const workItem = new WorkItem(data);

    workItem.mrn = this.dataTransformationService.filterDomainIdentifier(
      this.appConfigService.getUserDefaultDomain(),
      workItem.patientIdentifier || []
    );
    if (this.appConfigService.isNHIEnabled()) {
      workItem.nhi = this.dataTransformationService.filterDomainIdentifier(
        this.appConfigService.getNHIDomainName(),
        workItem.patientIdentifier
      );
    }
    workItem.archiveFetchStatus = this.archiveAdoptedService.getArchiveFetchStatus(
      workItem.id,
      UtilService.fetchStudyDataParam(workItem)
    );

    if (typeof data === 'object') {
      //Local Cpacs Exam
      if (!!data.workItem && !data.workItem.repositoryType) {
        this.handleDomainSysCode(data.workItem);
      }
    }
    return workItem;
  }

  isLaunchOfflinePopup(workItem) {
    let launchOfflinePopup = false;
    const archiveStatus = this.archiveAdoptedService.getArchiveFetchStatus(
      workItem.id,
      UtilService.fetchStudyDataParam(workItem)
    );
    if (
      archiveStatus === appConstants.archiveAdoptionStatus.ArchiveAdopted ||
      archiveStatus === appConstants.archiveAdoptionStatus.InProgress ||
      archiveStatus === appConstants.studyDataAvailability.THIRD_PARTY
    ) {
      launchOfflinePopup = true;
    }
    return launchOfflinePopup;
  }

  updateFromMap(items: any[], updates: any) {
    let appliedAny = false;
    for (let i = 0; i !== items.length; ++i) {
      const item = items[i];
      const updateList = updates[item.id] || [];
      appliedAny = this.applyUpdatesToItem(updateList, item) || appliedAny;
    }
    return appliedAny;
  }

  applyUpdatesToItem(updates, item) {
    let appliedAny = false;
    for (let i = 0, length = updates.length; i !== length; ++i) {
      appliedAny = this.applyUpdateToItem(updates[i], item) || appliedAny;
    }
    return appliedAny;
  }

  applyUpdateToItem(update, item): boolean {
    if (update && item) {
      if (update instanceof StateUpdate) {
        item.state = update.newState;
        return true;
      } else if (update instanceof StatusUpdate) {
        item.status = update.status;
        item.statusId = update.statusId;
        item.customClass = update.customClass;
        if (!_.isUndefined(update.verifiedTimeOfExam)) {
          item.verifiedTimeOfExam = update.verifiedTimeOfExam;
        }
        return true;
      } else if (update instanceof ExamFlagsUpdate) {
        item.examFlags = update.examFlags;
        item.exceptions = update.examFlags;
        return true;
      } else if (update instanceof IndicatorUpdate) {
        item.examSaved = update.examSavedState;
        item.examNoteCount = update.examNoteCount;
        return true;
      } else if (update instanceof ExamTagUpdate) {
        item.examTagDetails = update.examTagDetails;
        return true;
      } else if (update instanceof DesignatedAuthorUpdate) {
        item.nextApproverId = update.nextApproverId;
        return true;
      }
    }
    return false;
  }

  private filterSortUserList(item, statusFlagList) {
    if (!item.state || !item.state.ownership) {
      return false;
    }

    const obj = _.filter(item.state.ownership.assignedToUser, obj => {
      return obj.extStatusFlag !== 9 && obj.extStatusFlag !== 1 && statusFlagList.indexOf(obj.extStatusFlag) > -1;
    });
    if (obj.length === 0) {
      return false;
    }
    return obj.sort((a, b) => {
      const textA = a.lastName.toUpperCase();
      const textB = b.lastName.toUpperCase();
      return textA < textB ? -1 : textA > textB ? 1 : 0;
    });
  }
}
